import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { termsApi } from '../services/api';
import { isPdfDocumentUrl } from '../utils/documentUrl';

/**
 * Blocking Provider Terms gate.
 *
 * Renders nothing but itself until the owner's accepted version matches the active release, so
 * a future version bump reuses this same path with no new code. Uploaded HTML is untrusted content
 * and this app shares an origin with /api/documents, so an HTML document is framed sandboxed; a
 * PDF is not, because a sandboxed frame renders no PDF at all in Chrome/Edge.
 */
const ProviderTermsGate = ({ locale, children }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [terms, setTerms] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [loadError, setLoadError] = useState(false);
  // The owner must have actually opened the document before they can accept it. The embedded
  // preview is a convenience, not proof of anything -- and PDFs do not render in every browser.
  const [opened, setOpened] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const next = await termsApi.getStatus();
      // apiRequest resolves null on a 401. Treat that as a failure, not as an answer -- a null
      // here would otherwise leave the gate spinning forever with no way out.
      if (!next || typeof next.needsAcceptance !== 'boolean') {
        setLoadError(true);
        setStatus(null);
        return;
      }
      setStatus(next);
      if (next.needsAcceptance) setTerms(await termsApi.getActive(locale));
    } catch {
      // FAIL CLOSED. Degrading to needsAcceptance=false would show the dashboard on any
      // transient error, and the owner would then hit raw 403s from the backend guard with no
      // acceptance UI and no explanation.
      setLoadError(true);
      setStatus(null);
    }
  }, [locale]);

  useEffect(() => { load(); }, [load]);

  // The status check runs once on mount, so a session left open across a publish would keep
  // failing writes silently. api.js turns a 403 PROVIDER_TERMS_REQUIRED into this event.
  useEffect(() => {
    const onTermsRequired = () => load();
    window.addEventListener('anipaw:terms-required', onTermsRequired);
    return () => window.removeEventListener('anipaw:terms-required', onTermsRequired);
  }, [load]);

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Stack spacing={2} alignItems="center">
          <Typography>{t('terms.loadFailed')}</Typography>
          <Button variant="contained" onClick={load}>{t('terms.retry')}</Button>
        </Stack>
      </Container>
    );
  }

  if (!status) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  if (!status.needsAcceptance) return children;

  const accept = async () => {
    setSubmitting(true);
    setAcceptError('');
    try {
      setStatus(await termsApi.accept(terms.version, terms.locale));
    } catch {
      setAcceptError(t('terms.acceptFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h5">{terms?.title || t('terms.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('terms.mustAccept', { version: terms?.version })}
          </Typography>
          {/* Open-in-new-tab stays the PRIMARY path even now that the frame renders: it is what
              gates the accept button, and a browser that still refuses to embed the document must
              not leave the owner stuck. */}
          {terms?.url && (
            <Button
              variant="outlined"
              component="a"
              href={terms.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpened(true)}
              data-testid="terms-open"
            >
              {t('terms.openDocument')}
            </Button>
          )}
          {terms?.url && (
            /* Sandboxed for HTML, NOT for PDF -- see isPdfDocumentUrl. A fully sandboxed frame
               renders a PDF as a grey broken-document box in Chrome/Edge, which is exactly what
               the owner was being asked to accept. */
            <Box
              component="iframe"
              title={terms.title || 'Provider Terms'}
              src={terms.url}
              sandbox={isPdfDocumentUrl(terms.url) ? undefined : ''}
              sx={{ width: '100%', height: 480, border: '1px solid', borderColor: 'divider' }}
            />
          )}
          {acceptError && <Typography color="error">{acceptError}</Typography>}
          <Button
            data-testid="terms-accept"
            variant="contained"
            disabled={submitting || !terms?.version || !opened}
            onClick={accept}
          >
            {submitting ? <CircularProgress size={22} /> : t('terms.acceptButton')}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default ProviderTermsGate;
